import { PaginationLinksModel } from './PaginationLinks';
import { LeadModel } from '@/typings/models/Lead';
import { AssigneeModel } from '@/typings/models/Assignee';
import { AgentModel, BranchModel, ServiceModel } from '@/typings/models/Location';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';
import { CustomerModel } from '@/typings/models/Customer';
import { ServiceTypeModel } from '@/typings/models/ServiceType';
import { RecurringModel } from '@/typings/models/Recurrence';

export const ServiceRequestStatus = {
  confirmed: 'confirmed',
  tentative: 'tentative',
  rescheduled: 'rescheduled',
  declined: 'declined',
  cancelled_by_customer: 'cancelled_by_customer',
  cancelled: 'cancelled',
  rescheduled_by_customer: 'rescheduled_by_customer'
}
export interface ServiceRequestStyleModel {
  feature: string,
  hash_id: string,
  parameter: string,
  value: string
}

export interface VideoCallModel {
  meeting_id: string,
  guest_join_url: string
  host_join_url: string
}

/** GET /service-requests/calendar-unavailability item: a booking that keeps an
 * agent busy on a branch service outside the current calendar filter. */
export interface CalendarUnavailabilityModel {
  title: string,
  utc_start_time: string,
  utc_end_time: string,
  /** The agent's user uuid — same id space as ServiceRequestModel.agent.id. */
  agent?: string,
}
export interface ServiceRequestModel {
  id: string,
  created: string
  modified?: string
  when: string
  title: string
  what: string
  where: string
  status: keyof typeof ServiceRequestStatus,
  agent: AgentModel,
  branch?: BranchModel,
  attendance_status: 'show' | 'no_show' | null,
  service: {
    key: string,
    label: string
  },
  result?: {
    id: string,
    doi_date: string | null
    feedback: string | null
    styles: ServiceRequestStyleModel[]
  },
  utc_start_time: string,
  utc_end_time: string,
  real_start_time?: string,
  real_end_time?: string,
  videoCall: boolean,
  video_call?: VideoCallModel,
  lead?: LeadModel,
  branch_service?: ServiceModel,
  branch_service_id?: string
  payload: {
    start: string,
    end: string,
    what: string
  },
  extra_fields?: {
    [extraFieldTypeId: string]: {
      label: string,
      values: any[]
    }
  },
  service_request_extra_fields?: ExtraFieldTypeModel[]
  notAvailable?: boolean
  isDraggable?: boolean
  isResizable?: boolean
  isEditable?: boolean
  blocker?: boolean
  /** Custom calendar events only: the parent series when this event is an occurrence of one. */
  recurring?: RecurringModel | null
  eventColor?: string
  migration_source?: string
  migration_id?: string
  cancellation_reason?: string
  sync_role?: string
  isExternal?: boolean
  /** When isExternal: 'outlook' | 'googlecalendar' for calendar sync source */
  calendar_integration_type?: string
  /**
   * Custom calendar events only, and only on the detail response
   * (GET /calendar-events/:id): the user who created the event. Absent on the
   * list response, null for imported/legacy events.
   */
  creator?: AssigneeModel | null
  isHeightConstrained?: boolean
}
export interface CalendarEventModel {
  id: string,
  created: string
  utc_start_time: string,
  utc_end_time: string,
  real_start_time?: string,
  real_end_time?: string,
  title: string,
  description: string,
  lead: LeadModel,
  assignee: AssigneeModel
  /**
   * The user who created the event. Only returned by the detail endpoint
   * (GET /calendar-events/:id) — the list endpoint omits the key entirely —
   * and null for events imported from an external calendar.
   */
  creator?: AssigneeModel | null
  sync_role?: string
  /** When sync_role === 'external': 'outlook' | 'googlecalendar' */
  calendar_integration_type?: string
  /** The parent series when this event is an occurrence of one (CalendarEventResource#recurring), or null/undefined for a standalone event. */
  recurring?: RecurringModel | null
  /** @deprecated The backend now embeds the full `recurring` object instead of a bare id. */
  recurring_id?: string | null
}

export interface ServiceRequestTableData {
  items: ServiceRequestModel[]
}

export declare interface ServiceRequestPayload {
  state?: string
}

export declare interface RecentServiceRequestItem {
  Customer: {
    firstname: string
    links: {
      href: string
    },
    link: string,
    lastname: string | null
    id: string
    engagement_score?: number
    utm_medium?: string
    order_volume?: number
    state?: { label: string; value: string; hash: string }
  }
  Service: {
    type: string
  }
  ServiceRequest: {
    created: string
    id: string
    when?: string
    what?: string
    where?: string
    payload?: ServiceRequestPayload
  }
  State: {
    label: string
  }
}

export declare interface RecentServiceRequestModel {
  links: PaginationLinksModel
  items: RecentServiceRequestItem[]
  total: number
}


export declare interface MultiBookingModel {
  customer: CustomerModel,
  service_type: ServiceTypeModel,
  service_counts: number | string
}
